import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileXls, FilePdf, CaretDown, CaretUp, WarningCircle, PaperPlaneTilt, IdentificationCard } from "@phosphor-icons/react";
import { money } from "@/lib/money";

const shortDT = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

function PaymentsRow({ workerId, year, colSpan }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/year-end/1099/${workerId}/payments`, { params: { year } }).then((r) => setRows(r.data.payments)).catch(() => setRows([]));
  }, [workerId, year]);
  return (
    <tr className="bg-slate-50" data-testid={`ye-payments-${workerId}`}>
      <td colSpan={colSpan} className="px-4 py-3">
        {!rows ? <span className="text-xs text-slate-400">Loading…</span> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 uppercase tracking-wider">
              <th className="py-1 pr-3">Date paid</th><th className="py-1 pr-3">Pay week</th><th className="py-1 pr-3">Type</th>
              <th className="py-1 pr-3 text-right">Hours</th><th className="py-1 pr-3 text-right">Amount</th><th className="py-1 pr-3">Via</th>
            </tr></thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="py-1 pr-3 font-mono">{p.date}</td>
                  <td className="py-1 pr-3 font-mono">{p.week_start || "—"}</td>
                  <td className="py-1 pr-3">{p.kind_label}{p.note ? <span className="text-slate-400"> · {p.note}</span> : null}</td>
                  <td className="py-1 pr-3 text-right font-mono">{p.hours ? p.hours : ""}</td>
                  <td className={`py-1 pr-3 text-right font-mono ${p.amount < 0 ? "text-rose-600" : ""}`}>{money(p.amount)}</td>
                  <td className="py-1 pr-3 uppercase text-slate-500">{p.via}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  );
}

export default function YearEnd() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState("");
  const [showUnder, setShowUnder] = useState(false);
  const [emailing, setEmailing] = useState(""); // worker_id or "all"
  const [taxReqBusy, setTaxReqBusy] = useState("");

  const load = () => api.get("/year-end/1099", { params: { year } }).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail?.toString() || e.message));
  useEffect(() => { setData(null); setErr(""); load(); }, [year]);

  const requestTaxInfo = async (r) => {
    if (taxReqBusy) return;
    const again = r.tax_info_requested_at ? `\n\nAlready requested ${shortDT(r.tax_info_requested_at)}. Send again?` : "";
    if (!window.confirm(`Ask ${r.worker_name} for their missing ${r.missing.join(" and ")}?\n\nThey get an email${r.email ? "" : " (none on file)"} + text with a secure single-use link (14 days). You\u2019ll be emailed when they submit.${again}`)) return;
    setTaxReqBusy(r.worker_id);
    try {
      const { data: d } = await api.post(`/workers/${r.worker_id}/request-tax-info`);
      toast.success(`Tax info request sent to ${r.worker_name} — ${d.email_sent ? "email" : "no email"}${d.sms_sent ? " + text" : ""}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setTaxReqBusy(""); }
  };

  const emailOne = async (r) => {
    if (emailing) return;
    const again = r.last_emailed_at ? `\n\nAlready emailed ${shortDT(r.last_emailed_at)} to ${r.last_emailed_to}. Send again?` : "";
    if (!window.confirm(`Email ${r.worker_name} their ${year} payment summary PDF at ${r.email}?${again}`)) return;
    setEmailing(r.worker_id);
    try {
      const { data: d } = await api.post(`/year-end/1099/${r.worker_id}/email`, null, { params: { year } });
      toast.success(`${year} summary emailed to ${d.worker_name} (${d.to}).`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setEmailing(""); }
  };
  const emailAll = async () => {
    if (emailing || !data) return;
    const unsent = data.contractors.filter((r) => !r.last_emailed_at);
    const noEmail = unsent.filter((r) => !r.email).length;
    if (!window.confirm(`Email the ${year} payment summary PDF to ${unsent.length - noEmail} contractor(s) who haven't received it yet?${noEmail ? `\n\n${noEmail} will be skipped (no email on file).` : ""}\n\nContractors already emailed are skipped — use the per-row button to resend.`)) return;
    setEmailing("all");
    try {
      const { data: d } = await api.post("/year-end/1099/email-all", null, { params: { year } });
      if (d.sent.length) toast.success(`Emailed ${d.sent.length}: ${d.sent.map((s) => s.worker_name).join(", ")}`);
      else toast.info("Nothing sent — everyone was already emailed or has no email.");
      const real = d.skipped.filter((s) => s.reason !== "already emailed");
      if (real.length) toast.warning(`Skipped ${real.length}: ${real.map((s) => `${s.worker_name} (${s.reason})`).join("; ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setEmailing(""); }
  };

  const years = Array.from(new Set([...(data?.years_available || []), new Date().getFullYear(), new Date().getFullYear() - 1])).sort((a, b) => b - a);
  const colSpan = isAdmin ? 7 : 6;
  const unsentCount = data ? data.contractors.filter((r) => !r.last_emailed_at && r.email).length : 0;

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="tinylabel">Tax season</div>
          <h1 className="text-3xl font-black">Year-End 1099</h1>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">
            Cash-basis totals from <b>paid</b> pay runs — the year a contractor was paid, not the week worked. Owner distributions are excluded.
            Only contractors at or above the IRS 1099-NEC threshold are listed.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Tax year
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="ye-year-select"
              className="mt-1 block h-11 px-3 border border-slate-300 rounded-md bg-white font-semibold text-slate-900">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          {isAdmin && data && data.contractors.length > 0 && (
            <>
              <a href={`${API_BASE}/year-end/1099.xlsx?year=${year}`} data-testid="ye-download-xlsx"
                className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md flex items-center gap-2">
                <FileXls size={18} weight="bold" /> Excel for accountant
              </a>
              <a href={`${API_BASE}/year-end/1099-all.pdf?year=${year}`} target="_blank" rel="noreferrer" data-testid="ye-download-all-pdf"
                className="h-11 px-4 border border-slate-300 hover:bg-slate-50 font-semibold rounded-md flex items-center gap-2">
                <FilePdf size={18} weight="bold" /> All contractor PDFs
              </a>
              <button type="button" onClick={emailAll} disabled={!!emailing || unsentCount === 0} data-testid="ye-email-all"
                title={unsentCount ? "Email the PDF to every contractor who hasn't received it yet." : "Everyone on the list has already been emailed (or has no email)."}
                className="h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md flex items-center gap-2 disabled:opacity-50">
                <PaperPlaneTilt size={18} weight="bold" /> {emailing === "all" ? "Emailing…" : `Email all (${unsentCount} unsent)`}
              </button>
            </>
          )}
        </div>
      </div>

      {err && <div className="text-rose-600 font-semibold" data-testid="ye-error">{err}</div>}
      {!data && !err && <div className="text-slate-400">Loading…</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-md p-4"><div className="tinylabel">Contractors needing a 1099</div><div className="text-3xl font-black mt-1" data-testid="ye-kpi-count">{data.totals.contractors}</div></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><div className="tinylabel">Total paid ({year})</div><div className="text-3xl font-black mt-1 font-mono" data-testid="ye-kpi-total">{money(data.totals.amount)}</div></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><div className="tinylabel">1099-NEC threshold</div><div className="text-3xl font-black mt-1 font-mono" data-testid="ye-kpi-threshold">{money(data.threshold)}</div><div className="text-xs text-slate-500 mt-1">{year <= 2025 ? "IRS threshold for payments made through 2025" : "IRS threshold for payments made 2026 and later"}</div></div>
          </div>

          {!data.payer.tax_id && isAdmin && (
            <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-md p-3 text-sm text-amber-900" data-testid="ye-payer-ein-warning">
              <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
              <span>Your company EIN isn't set, so it won't print on the contractor PDFs. Add it under <Link to="/settings" className="font-semibold underline">Settings → Company</Link>.</span>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="ye-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-widest text-slate-500 font-bold">
                  <th className="px-4 py-3">Contractor</th><th className="px-4 py-3">Address</th><th className="px-4 py-3">Tax ID</th>
                  <th className="px-4 py-3 text-right">Total paid</th><th className="px-4 py-3 text-right">Payments</th><th className="px-4 py-3">Missing</th>
                  {isAdmin && <th className="px-4 py-3 text-right">PDF / Email</th>}
                </tr>
              </thead>
              <tbody>
                {data.contractors.length === 0 && (
                  <tr><td colSpan={colSpan} className="px-4 py-8 text-slate-400" data-testid="ye-empty">
                    No contractor was paid {money(data.threshold)} or more in {year}{data.under_threshold.length ? ` (${data.under_threshold.length} under the threshold — see below)` : ""}.
                  </td></tr>
                )}
                {data.contractors.map((r) => (
                  <>
                    <tr key={r.worker_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ye-row-${r.worker_id}`}>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setOpenId(openId === r.worker_id ? "" : r.worker_id)} data-testid={`ye-toggle-${r.worker_id}`}
                          className="font-semibold inline-flex items-center gap-1 hover:text-emerald-700">
                          {openId === r.worker_id ? <CaretUp size={12} /> : <CaretDown size={12} />}{r.worker_name}
                        </button>
                        {r.dba && <div className="text-xs text-slate-500">DBA {r.dba}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{r.address || <span className="text-rose-600">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.tax_id_last4 ? `${r.tax_id_kind || "TIN"} ···${r.tax_id_last4}` : <span className="text-rose-600">none</span>}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold" data-testid={`ye-total-${r.worker_id}`}>{money(r.total)}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.payments_count}</td>
                      <td className="px-4 py-3 text-xs">
                        {r.missing.length ? (
                          <div className="flex flex-col items-start gap-1">
                            <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold" data-testid={`ye-missing-${r.worker_id}`}>Missing {r.missing.join(" + ")}</span>
                            {isAdmin && (
                              <button type="button" onClick={() => requestTaxInfo(r)} disabled={!!taxReqBusy || !r.can_request} data-testid={`ye-request-tax-${r.worker_id}`}
                                title={!r.can_request ? "No email or phone on file — add one on the Workers tab." : "Send a secure single-use link so they can fill in what's missing."}
                                className="inline-flex items-center gap-1 font-semibold text-amber-800 hover:text-amber-950 disabled:opacity-40">
                                <IdentificationCard size={12} weight="bold" />
                                {taxReqBusy === r.worker_id ? "Sending…" : (r.tax_info_requested_at ? `Re-request (sent ${shortDT(r.tax_info_requested_at)})` : "Request tax info")}
                              </button>
                            )}
                          </div>
                        ) : <span className="text-emerald-700 font-semibold">Complete</span>}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <a href={`${API_BASE}/year-end/1099/${r.worker_id}.pdf?year=${year}`} target="_blank" rel="noreferrer" data-testid={`ye-pdf-${r.worker_id}`}
                            className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-semibold"><FilePdf size={14} weight="bold" /> PDF</a>
                          <button type="button" onClick={() => emailOne(r)} disabled={!!emailing || !r.email} data-testid={`ye-email-${r.worker_id}`}
                            title={!r.email ? "No email on file — add one on the Workers tab." : (r.last_emailed_at ? `Emailed ${shortDT(r.last_emailed_at)} to ${r.last_emailed_to}. Click to send again.` : `Email the PDF to ${r.email}`)}
                            className={`ml-3 inline-flex items-center gap-1 font-semibold disabled:opacity-40 ${r.last_emailed_at ? "text-emerald-700 hover:text-emerald-900" : "text-slate-900 hover:text-emerald-700"}`}>
                            <PaperPlaneTilt size={14} weight="bold" />
                            {emailing === r.worker_id ? "Sending…" : (r.last_emailed_at ? "Resend" : "Email PDF")}
                          </button>
                          {r.last_emailed_at && <div className="text-[11px] text-emerald-700 mt-0.5" data-testid={`ye-emailed-${r.worker_id}`}>Sent {shortDT(r.last_emailed_at)}{r.email_count > 1 ? ` (${r.email_count}×)` : ""}</div>}
                          {!r.email && <div className="text-[11px] text-rose-600 mt-0.5">no email</div>}
                        </td>
                      )}
                    </tr>
                    {openId === r.worker_id && <PaymentsRow key={`${r.worker_id}-p`} workerId={r.worker_id} year={year} colSpan={colSpan} />}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {data.under_threshold.length > 0 && (
            <div className="mt-4 text-sm">
              <button type="button" onClick={() => setShowUnder((v) => !v)} data-testid="ye-under-toggle" className="text-slate-600 hover:text-slate-900 font-semibold inline-flex items-center gap-1">
                {showUnder ? <CaretUp size={12} /> : <CaretDown size={12} />}
                {data.under_threshold.length} contractor{data.under_threshold.length === 1 ? "" : "s"} under the {money(data.threshold)} threshold — no 1099 required
              </button>
              {showUnder && (
                <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 text-xs text-slate-600" data-testid="ye-under-list">
                  {data.under_threshold.map((u) => <li key={u.worker_id} className="flex justify-between border border-slate-200 rounded px-3 py-1.5 bg-white"><span>{u.worker_name}</span><span className="font-mono">{money(u.total)}</span></li>)}
                </ul>
              )}
            </div>
          )}
          <p className="mt-6 text-xs text-slate-400">Every Excel/PDF download is recorded in the Audit Trail. The Excel includes full tax IDs for filing; contractor PDFs show the last 4 digits only. Fix missing tax IDs/addresses on the <Link to="/workers" className="underline">Workers</Link> tab, then re-download.</p>
        </>
      )}
    </div>
  );
}
